import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/dist/client/router';

import { Text } from '@sitecore-jss/sitecore-jss-nextjs';

import useSearchParams from 'src/hooks/useSearchParams';

import { ComponentProps } from 'lib/ComponentProps';

import stripHtml from 'src/utils/stripHtml';

import { Button } from 'src/components/basicContent/_Button/Button';

import CloseIconSvg from 'public/icons/close.svg';
import SearchSvg from 'public/icons/search.svg';

import { SearchBarFields } from './SearchBarFields';

import styles from './SearchBar.module.css';

type SearchBarProps = ComponentProps & {
	expandable?: boolean;
	integrated?: boolean;
	buttonColour?: string;
	fields?: SearchBarFields;
};

type Inputs = {
	Keyword: string;
};

export default function SearchBar(props: SearchBarProps): JSX.Element {
	const router = useRouter();

	const queryParams = new URLSearchParams(router.asPath.substring(router.asPath.lastIndexOf('?') + 1));
	const defaultKeyword: string | undefined = queryParams.get('q')?.trim() ?? undefined;

	const [isActive, setIsActive] = useState(false);
	const [isMobile, setIsMobile] = useState(false);

	const submitText = useRef<HTMLDivElement>(null);
	const searchInput = useRef<HTMLInputElement | null>(null);
	const params = useSearchParams();

	const { register, handleSubmit, setValue, getValues } = useForm<Inputs>();
	const { ref, ...rest } = register('Keyword');

	const redirectToSearch = (data: { Keyword: string }) => {
		if (isMobile && submitText.current) {
			submitText.current.style.width = '';
		}

		router.push({
			pathname: `${props.fields?.SearchResultsPage?.value?.href}`,
			query: {
				...router.query,
				q: data.Keyword ? stripHtml(data.Keyword.trim()) : '',
				fromStandaloneSearch: 'true',
			},
		});
	};

	const placeholderText = useMemo(() => {
		const desktopText = props.fields?.SearchPlaceholderText?.value;
		const mobileText = props.fields?.SearchPlaceholderTextMobile?.value;

		return isMobile && mobileText ? mobileText : desktopText;
	}, [isMobile]);

	useEffect(() => {
		if (!submitText.current || !searchInput.current || !props.expandable || isMobile) {
			return;
		}

		submitText.current.style.width = isActive ? `${submitText.current.scrollWidth}px` : '';

		searchInput.current?.focus();
	}, [isActive, isMobile, props.expandable]);

	useEffect(() => {
		window.onresize = () => setIsMobile(window.innerWidth < 1000);

		setIsMobile(window.innerWidth < 1000);
	}, []);

	useEffect(() => {
		if (getValues('Keyword') !== params.q) {
			setValue('Keyword', params.q);
		}
	}, [getValues, params.q, router, setValue]);

	return (
		<section className={`${props.integrated ? '' : 'padX maxWidth'} ${props.expandable ? styles.inline : ''}`}>
			<form
				className={`${styles.form} ${props.expandable ? styles.expandable : ''} ${styles.adaptive} ${isActive ? 'active' : ''} rounded`}
				onMouseEnter={props.expandable ? () => setIsActive(true) : undefined}
				onMouseLeave={props.expandable ? () => setIsActive(false) : undefined}
				onSubmit={handleSubmit(redirectToSearch)}
			>
				<div className={styles.fieldset}>
					<label className="screenReaderOnly" htmlFor="search-input">
						{props.fields && <Text field={props.fields.SearchButtonText} />}
					</label>

					<input
						className={`${styles.searchInput} greyLight`}
						type="search"
						id="search-input"
						autoComplete="off"
						data-hj-allow
						placeholder={placeholderText}
						defaultValue={defaultKeyword}
						{...rest}
						name="Keyword"
						ref={(e) => {
							ref(e);
							searchInput.current = e;
						}}
					/>

					{getValues('Keyword') && (getValues('Keyword') as string).length > 0 && (
						<Button
							title="Close Icon"
							type="submit"
							className={styles.clearKeyword}
							onClick={() => {
								setValue('Keyword', '');
								handleSubmit(redirectToSearch);
							}}
						>
							<CloseIconSvg className={styles.clearKeywordIcon} />
						</Button>
					)}
				</div>

				<Button
					type="submit"
					title="Search"
					className={`${styles.button} button gridCenter ${props.buttonColour ? props.buttonColour : 'pinkDark'}`}
				>
					<SearchSvg className={styles.icon} />

					<div className={styles.submitText} ref={submitText}>
						<span className={styles.submitTextInner}>
							{props.fields?.SearchButtonText ? props.fields.SearchButtonText.value : 'Search'}
						</span>
					</div>
				</Button>
			</form>
		</section>
	);
}
